import * as XLSX from 'xlsx';

/// Uma linha pronta para a API; line é a linha do arquivo, para a conferência apontar o problema.
export interface SpreadsheetPersonRow {
    line: number;
    name: string;
    phone: string;
    email: string;
    notes: string;
}

export const IMPORT_FIELDS = ['name', 'phone', 'email', 'notes'] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number];

/// Índice da coluna da planilha para cada campo, ou null quando não importa.
export type ColumnMapping = Record<ImportField, number | null>;

/// Uma linha do arquivo como veio, com o número que ela tem no Excel.
export interface SheetRow {
    line: number;
    cells: string[];
}

/// A planilha a partir do cabeçalho: headers é a linha escolhida, rows são as de baixo.
export interface Spreadsheet {
    headerLine: number;
    headers: string[];
    rows: SheetRow[];
}

/// Nomes de cabeçalho que cada campo reconhece, sem acento, espaço e pontuação.
const ALIASES: Record<ImportField, string[]> = {
    name: ['nome', 'nomecompleto', 'name', 'cliente', 'contato', 'nomedocliente', 'nomedocontato'],
    phone: ['telefone', 'celular', 'whatsapp', 'whats', 'fone', 'tel', 'phone', 'telefonecelular'],
    email: ['email', 'correioeletronico'],
    notes: ['observacoes', 'observacao', 'obs', 'notas', 'anotacoes', 'notes'],
};

/// Até onde procurar o cabeçalho: título e explicações no topo são comuns, vinte linhas delas não.
const HEADER_SEARCH_ROWS = 20;

const normalize = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

/// Inteiro sem notação científica: celular com DDI tem 13 dígitos e "5.51199E+12" não é telefone.
export const cellText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return Number.isInteger(value) ? value.toFixed(0) : String(value);
    return String(value).trim();
};

const filled = (row: SheetRow) => row.cells.filter((cell) => cell !== '').length;

/// As linhas da primeira aba, sem as em branco, com o número de linha que têm no arquivo.
export const readRows = (workbook: XLSX.WorkBook): SheetRow[] => {
    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    if (!sheet || !sheet['!ref']) return [];

    const firstRow = XLSX.utils.decode_range(sheet['!ref']).s.r;

    const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        raw: true,
        defval: '',
        blankrows: true,
    });

    return matrix
        .map((cells, index) => ({ line: firstRow + index + 1, cells: cells.map(cellText) }))
        .filter((row) => filled(row) > 0);
};

/// Cada cabeçalho serve a um campo só: nome exato primeiro, depois o que o contém.
export const guessMapping = (headers: string[]): ColumnMapping => {
    const normalized = headers.map(normalize);
    const used = new Set<number>();
    const mapping = Object.fromEntries(IMPORT_FIELDS.map((field) => [field, null])) as ColumnMapping;

    const find = (field: ImportField, match: (header: string, alias: string) => boolean) => {
        if (mapping[field] !== null) return;

        const index = normalized.findIndex(
            (header, i) => !used.has(i) && header !== '' && ALIASES[field].some((alias) => match(header, alias))
        );

        if (index === -1) return;

        mapping[field] = index;
        used.add(index);
    };

    IMPORT_FIELDS.forEach((field) => find(field, (header, alias) => header === alias));
    IMPORT_FIELDS.forEach((field) =>
        find(field, (header, alias) => alias.length >= 4 && header.includes(alias))
    );

    return mapping;
};

const recognized = (row: SheetRow) =>
    Object.values(guessMapping(row.cells)).filter((index) => index !== null).length;

/// A linha do cabeçalho: a com mais campos reconhecidos; sem nenhum, a com mais células preenchidas.
export const detectHeaderLine = (rows: SheetRow[]): number | null => {
    const candidates = rows.slice(0, HEADER_SEARCH_ROWS);

    if (candidates.length === 0) return null;

    const best = (score: (row: SheetRow) => number) =>
        candidates.reduce((winner, row) => (score(row) > score(winner) ? row : winner));

    const byName = best(recognized);

    return recognized(byName) > 0 ? byName.line : best(filled).line;
};

/// Coluna sem cabeçalho e sem valor sai: célula mesclada do título alarga a planilha à toa.
export const withHeader = (rows: SheetRow[], headerLine: number): Spreadsheet => {
    const header = rows.find((row) => row.line === headerLine);
    const below = rows.filter((row) => row.line > headerLine);

    const width = Math.max(header?.cells.length ?? 0, ...below.map((row) => row.cells.length));

    const kept = Array.from({ length: width }, (_, column) => column).filter(
        (column) =>
            (header?.cells[column] ?? '') !== '' || below.some((row) => (row.cells[column] ?? '') !== '')
    );

    const pick = (cells: string[]) => kept.map((column) => cells[column] ?? '');

    return {
        headerLine,
        headers: pick(header?.cells ?? []),
        rows: below.map((row) => ({ line: row.line, cells: pick(row.cells) })),
    };
};

/// Os primeiros valores da coluna, para escolher sem abrir o arquivo.
export const columnSamples = (sheet: Spreadsheet, column: number, max = 3): string[] =>
    sheet.rows
        .map((row) => row.cells[column] ?? '')
        .filter((value) => value !== '')
        .slice(0, max);

/// Excel no Brasil salva CSV em Windows-1252; ler como UTF-8 transforma "João" em "Jo�o".
export const decodeText = (bytes: ArrayBuffer): string => {
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        return new TextDecoder('windows-1252').decode(bytes);
    }
};

export const readSpreadsheetRows = async (file: File): Promise<SheetRow[]> => {
    const buffer = await file.arrayBuffer();

    const workbook = /\.csv$/i.test(file.name)
        ? XLSX.read(decodeText(buffer), { type: 'string' })
        : XLSX.read(buffer, { type: 'array' });

    return readRows(workbook);
};

export const toPersonRows = (
    sheet: Spreadsheet,
    mapping: ColumnMapping
): SpreadsheetPersonRow[] => {
    const pick = (cells: string[], field: ImportField) =>
        mapping[field] === null ? '' : cells[mapping[field] as number] ?? '';

    return sheet.rows
        .map(({ line, cells }) => ({
            line,
            name: pick(cells, 'name'),
            phone: pick(cells, 'phone'),
            email: pick(cells, 'email'),
            notes: pick(cells, 'notes'),
        }))
        .filter((row) => IMPORT_FIELDS.some((field) => row[field] !== ''));
};
